// Bezeichnungen, Zustände und Schlüssel des Verkaufs – für Server UND Oberfläche (keine Abhängigkeit zum Kern).

export const QUOTE_TYPE = 'quote'
export const ORDER_TYPE = 'sales_order'
export const DELIVERY_TYPE = 'delivery_note'
export const RETURN_TYPE = 'sales_return'
export const CONSIGNMENT_DELIVERY_TYPE = 'consignment_delivery'
export const CONSIGNMENT_RETURN_TYPE = 'consignment_return'

export const SALES_DOCUMENT_TYPES = [QUOTE_TYPE, ORDER_TYPE, DELIVERY_TYPE, RETURN_TYPE, CONSIGNMENT_DELIVERY_TYPE, CONSIGNMENT_RETURN_TYPE] as const

export const QUOTE_STATUS = {
  draft: 'draft',
  open: 'open',
  partiallyOrdered: 'partially_ordered',
  ordered: 'ordered',
  rejected: 'rejected',
  expired: 'expired',
} as const

export const QUOTE_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  open: 'Offen',
  partially_ordered: 'Teilweise beauftragt',
  ordered: 'Beauftragt',
  rejected: 'Abgelehnt',
  expired: 'Abgelaufen',
  cancelled: 'Zurückgezogen',
}

export const ORDER_STATUS = {
  draft: 'draft',
  /** über einen Kanal eingegangen, noch nicht bestätigt */
  received: 'pending_review',
  /** Rabatt über der Grenze der erfassenden Person */
  pending: 'pending_approval',
  approved: 'approved',
  /** aus Freigabe oder Kreditsperre zurück beim Vertrieb – wieder bearbeitbar */
  returned: 'returned',
  /** über dem Kreditlimit, wartet auf jemanden mit dem Recht zur Freigabe */
  creditHold: 'pending_credit',
  confirmed: 'confirmed',
  partiallyDelivered: 'partially_delivered',
  delivered: 'delivered',
  closed: 'closed',
} as const

export const ORDER_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  pending_review: 'Eingegangen',
  pending_approval: 'Rabatt zur Freigabe',
  approved: 'Rabatt freigegeben',
  returned: 'In Überarbeitung',
  pending_credit: 'Kreditsperre',
  confirmed: 'Bestätigt',
  partially_delivered: 'Teilgeliefert',
  delivered: 'Geliefert',
  closed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
}

/** bestätigt und noch nicht vollständig geliefert */
export const ORDER_OPEN_STATUSES = [ORDER_STATUS.confirmed, ORDER_STATUS.partiallyDelivered] as const
/** vor der Bestätigung gesperrt: ändern erst nach „Zurück in Bearbeitung“ */
export const ORDER_LOCKED_STATUSES = [ORDER_STATUS.pending, ORDER_STATUS.approved, ORDER_STATUS.creditHold] as const
export const ORDER_UNCONFIRMED_STATUSES = [ORDER_STATUS.draft, ORDER_STATUS.received, ORDER_STATUS.returned, ORDER_STATUS.pending, ORDER_STATUS.approved, ORDER_STATUS.creditHold] as const

export const DELIVERY_STATUS = {
  draft: 'draft',
  ready: 'ready',
  inTransit: 'in_transit',
  delivered: 'delivered',
  notDelivered: 'not_delivered',
  partiallyInvoiced: 'partially_invoiced',
  invoiced: 'invoiced',
} as const

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  draft: 'In Kommissionierung',
  ready: 'Lieferbereit',
  in_transit: 'Unterwegs',
  delivered: 'Zugestellt',
  not_delivered: 'Nicht zugestellt',
  partially_invoiced: 'Teilweise berechnet',
  invoiced: 'Berechnet',
  reversed: 'Storniert',
}

/** gebucht, aber noch nicht beim Kunden */
export const DELIVERY_OUTBOUND_STATUSES = [DELIVERY_STATUS.ready, DELIVERY_STATUS.inTransit] as const
/** zugestellt und (noch) nicht vollständig berechnet – daraus arbeitet das Modul Finanzen */
export const DELIVERY_BILLABLE_STATUSES = [DELIVERY_STATUS.delivered, DELIVERY_STATUS.partiallyInvoiced] as const

export const RETURN_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  posted: 'Gebucht',
  partially_credited: 'Teilweise gutgeschrieben',
  credited: 'Gutgeschrieben',
  reversed: 'Storniert',
}

export const CONSIGNMENT_STATUS = { out: 'out', settled: 'settled' } as const
export const CONSIGNMENT_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  out: 'Beim Kunden',
  settled: 'Abgerechnet',
  posted: 'Gebucht',
  reversed: 'Storniert',
}

/** Zusatzfelder am Auftragskopf (document.custom_fields) */
export const ORDER_FIELDS = {
  /** Zeitfenster der Lieferung 'HH:MM' */
  timeFrom: 'timeFrom',
  timeTo: 'timeTo',
  pickingNote: 'pickingNote',
  driverNote: 'driverNote',
  /** geplantes Fahrzeug (location.id) */
  vehicleLocationId: 'vehicleLocationId',
  /** Herkunft: Schlüssel aus SALES_CHANNELS */
  channel: 'channel',
  /** Bestellnummer des Kanals für Menschen („AL-24851“) */
  channelNumber: 'channelNumber',
  /** standard | consignment */
  kind: 'orderKind',
  /** Einmalkunde: { name, street, zip, city, phone, email } */
  oneTimeCustomer: 'oneTimeCustomer',
  /** vom Kunden angemeldetes Leergut: [{ label, qty }] – Hinweis für den Fahrer */
  announcedEmpties: 'announcedEmpties',
  /** Angebot: gültig bis */
  validUntil: 'validUntil',
  /** Kommission: Tag der Rückzählung */
  eventEnd: 'eventEnd',
  /** Bestell-App: Lieferanschrift { name, street, zip, city, note?, phone? } – auf Lieferschein und in der Fahreransicht */
  shipTo: 'shipTo',
  /** Bestell-App: Kostenstelle des Geschäftskunden */
  costCenter: 'costCenter',
  /** Bestell-App: Herkunft in der App – app | phone | subscription */
  channelOrigin: 'channelOrigin',
  /** Bestell-App: gewählte Zahlart – cash | ec | paypal | card | invoice | sepa */
  channelPayment: 'channelPayment',
  /** Bestell-App: Bestellung auf Kommission gewünscht (Hinweis für den Vertrieb) */
  channelCommission: 'channelCommission',
  /** Bestell-App: bestellte Leihartikel [{ sku, name, qty (Stück), eventDate, unitPriceCents }] – bucht der Vertrieb */
  channelRentals: 'channelRentals',
  /** Bestell-App: delivery | pickup */
  channelFulfillment: 'channelFulfillment',
  /** Bestell-App: Ergebnis der Prüfung der Lieferregeln { zoneId, couponCode, placedAt, issues[] } (docs/integration/bestell-app.md §19.2) */
  checkout: 'checkout',
  /**
   * Telefonassistent: Angaben der Telefon-Bestellung { accessId, callbackPhone, phoneSource, phoneConfirmed, ageConfirmed, callRef,
   * placedAt, late, contactName, hints[] } (docs/integration/telefonassistent.md §16) – Hinweise stehen hier, nicht in `checkout.issues`
   */
  phoneOrder: 'phoneOrder',
} as const

/** Zusatzfeld an Gebühren- und Rabattzeilen eines Auftrags der Bestell-App: delivery | carry | coupon | rental */
export const CHANNEL_CHARGE_FIELD = 'channelCharge'
/** Zusatzfeld der Zeile: Nummer der Gebühr – eine Gebühr kann je Steuersatz auf mehrere Zeilen verteilt sein (`chargeLinesFor`) */
export const CHANNEL_CHARGE_INDEX = 'channelChargeIndex'

export const CHANNEL_ORIGIN_LABELS: Record<string, string> = { app: 'Online-Shop', phone: 'Telefon (über die App erfasst)', subscription: 'Abo' }
export const CHANNEL_PAYMENT_LABELS: Record<string, string> = { cash: 'bar bei Lieferung', ec: 'EC-Karte bei Lieferung', paypal: 'PayPal', card: 'Kreditkarte', invoice: 'auf Rechnung', sepa: 'SEPA-Lastschrift' }

/** Zusatzfelder an Lieferschein-Zeilen */
export const DELIVERY_LINE_FIELDS = {
  /** offene Menge des Auftrags beim Kommissionieren (Tausendstel des Gebindes) */
  orderedQty: 'orderedQty',
  /** nicht kommissionierte Menge (Fehlmenge) */
  shortQty: 'shortQty',
  shortReason: 'shortReason',
  /** Grund, warum eine andere Charge als vorgeschlagen gepackt wurde (docs/kommissionieren/SPEZIFIKATION.md K7) */
  lotReason: 'lotReason',
} as const

export const ORDER_KINDS = ['standard', 'consignment'] as const
export type OrderKind = (typeof ORDER_KINDS)[number]
export const ORDER_KIND_LABELS: Record<OrderKind, string> = { standard: 'Lieferung', consignment: 'Kommission' }

/** `phone` = vom Personal am Telefon erfasst; `phone_assistant` = vom Telefonassistenten (Vocaris) unbestätigt angelegt (telefonassistent.md §16) */
export const SALES_CHANNELS = ['desk', 'phone', 'app', 'phone_assistant', 'mail'] as const
export type SalesChannel = (typeof SALES_CHANNELS)[number]
export const SALES_CHANNEL_LABELS: Record<SalesChannel, string> = { desk: 'Vertrieb', phone: 'Telefon', app: 'Bestell-App', phone_assistant: 'Telefon (KI)', mail: 'E-Mail' }
/** Bezeichnung der Herkunft „Telefonassistent“ – Plakette in Aufträgen, Abholungen, Disposition und Widget */
export const PHONE_ASSISTANT_LABEL = SALES_CHANNEL_LABELS.phone_assistant
export const isSalesChannel = (value: unknown): value is SalesChannel => typeof value === 'string' && (SALES_CHANNELS as readonly string[]).includes(value)
export const channelLabel = (value: unknown): string | null => (isSalesChannel(value) ? SALES_CHANNEL_LABELS[value] : null)

export const DELIVERY_OUTCOMES = ['delivered', 'differences', 'not_delivered'] as const
export type DeliveryOutcome = (typeof DELIVERY_OUTCOMES)[number]
export const DELIVERY_OUTCOME_LABELS: Record<DeliveryOutcome, string> = {
  delivered: 'Zugestellt wie notiert',
  differences: 'Zugestellt mit Abweichungen',
  not_delivered: 'Nicht zugestellt',
}

export const RETURN_DISPOSITIONS = ['stock', 'blocked'] as const
export type ReturnDisposition = (typeof RETURN_DISPOSITIONS)[number]
export const RETURN_DISPOSITION_LABELS: Record<ReturnDisposition, string> = { stock: 'Zurück in den Bestand', blocked: 'In den Sperrbestand' }

export const RENTAL_STATUS_LABELS: Record<string, string> = {
  requested: 'Angefragt',
  confirmed: 'Gebucht',
  out: 'Beim Kunden',
  returned: 'Zurück',
  cancelled: 'Storniert',
}
/** Buchungen, die Geräte für ihren Zeitraum binden */
export const RENTAL_BLOCKING_STATUSES = ['requested', 'confirmed', 'out'] as const

export const TASK_KINDS = {
  orderIntake: 'sales.order.intake',
  discountApproval: 'sales.order.discount',
  creditHold: 'sales.order.credit',
  backorder: 'sales.order.backorder',
  /** Bestell-App: Zustellung gescheitert – bitte neu einplanen */
  deliveryFailed: 'sales.channel.deliveryFailed',
  /** Bestell-App: bestellte Leihartikel buchen (keine automatische Verleihbuchung) */
  rentalBooking: 'sales.channel.rentalBooking',
  /** Bestell-App: an der Tür mehr kassiert als fällig – für die Buchhaltung */
  overpaid: 'sales.channel.overpaid',
  /** Disposition: Tour gestartet, der Auftrag eines Stopps ist noch nicht kommissioniert */
  tourNotPicked: 'sales.tour.notPicked',
  /** Disposition: Tour beendet, der Lieferschein eines Stopps ist noch „Unterwegs“ (Nacharbeit N2) */
  tourEndedInTransit: 'sales.tour.endedInTransit',
  /** Meldung der Bestell-App, die das Personal erledigen muss (Abo, Übertragung, Zustellung, Anbindung – §20 S4) */
  channelNotice: 'sales.channel.notice',
  /** Antrag der Bestell-App (Geschäftskunde, Löschung), über den das Personal in Merxo entscheidet (§20 S5) */
  channelCase: 'sales.channel.case',
  /** Die Bestell-App konnte eine Entscheidung oder einen Auftrag nicht ausführen (§20 S5) */
  channelCaseFailed: 'sales.channel.caseFailed',
  /** Interessent aus dem Telefonassistenten – bitte melden (docs/integration/telefonassistent.md §6); ohne Personendaten im Text */
  phoneLead: 'sales.phone.lead',
  /** Anfrage per E-Mail – bitte prüfen und weiterverarbeiten (Anfragen-Zentrale); ohne Personendaten im Text */
  mailRequest: 'sales.mail.request',
  /** Auffällig viele Fehlversuche bei der Auftragsauskunft des Telefonassistenten (§7, höchstens eine je Stunde) */
  phoneLookupAlert: 'sales.phone.lookupAlert',
  /** Auffällig viele Telefon-Bestellungen eines Zugangs (§16, höchstens eine je Stunde) */
  phoneOrderAlert: 'sales.phone.orderAlert',
  /** Auffällig viele Fehlversuche bei der Kundenprüfung des Telefonassistenten (§16, höchstens eine je Stunde) */
  phoneCustomerAlert: 'sales.phone.customerAlert',
} as const

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
export const isTime = (value: unknown): value is string => typeof value === 'string' && TIME.test(value)

/** „08:00–12:00 Uhr“, „ab 14:00 Uhr“ oder null */
export function timeWindowText(from: unknown, to: unknown): string | null {
  const a = isTime(from) ? from : null
  const b = isTime(to) ? to : null
  if (a && b) return `${a}–${b} Uhr`
  if (a) return `ab ${a} Uhr`
  if (b) return `bis ${b} Uhr`
  return null
}

/** Hinweis zur Alterskontrolle nach dem höchsten Mindestalter der Lieferung (§ 9 JuSchG) – null ohne Altersgrenze. */
export function ageNotice(minAge: number | null | undefined): string | null {
  if (!minAge || minAge <= 0) return null
  return minAge >= 18
    ? 'Abgabe nur an Personen ab 18 Jahren. Bitte bei der Übergabe das Alter kontrollieren.'
    : 'Abgabe nur an Personen ab 16 Jahren. Bitte bei der Übergabe das Alter kontrollieren.'
}

// ---- Disposition (docs/integration/bestell-app.md §18) -------------------------------------------------------------

export const TOUR_STATUSES = ['planned', 'released', 'active', 'completed', 'cancelled'] as const
export type TourStatus = (typeof TOUR_STATUSES)[number]
export const TOUR_STATUS_LABELS: Record<TourStatus, string> = {
  planned: 'Geplant',
  released: 'Freigegeben',
  active: 'Unterwegs',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
}
/** Touren, auf denen Stopps stehen dürfen und Aufträge als „verplant“ gelten */
export const TOUR_OPEN_STATUSES = ['planned', 'released', 'active'] as const
/** Touren, die die App sieht und ausführt */
export const TOUR_VISIBLE_STATUSES = ['released', 'active'] as const

export const STOP_STATUSES = ['pending', 'arrived', 'delivered', 'failed'] as const
export type StopStatus = (typeof STOP_STATUSES)[number]
export const STOP_STATUS_LABELS: Record<StopStatus, string> = {
  pending: 'Offen',
  arrived: 'Angekommen',
  delivered: 'Zugestellt',
  failed: 'Nicht zugestellt',
}
/** angefahren, zugestellt oder gescheitert: Position und Auftrag eines Stopps sind fest (T4) */
export const STOP_FIXED_STATUSES = ['arrived', 'delivered', 'failed'] as const

export const TOUR_WARNING_CODES = ['overload', 'window_late', 'no_location', 'approx_location', 'max_stops'] as const
export type TourWarningCode = (typeof TOUR_WARNING_CODES)[number]

/** Herkunft einer Ortsangabe (sales_geocode.source) */
export const GEOCODE_SOURCES = ['manual', 'app', 'geocoder', 'approx'] as const
export type GeocodeSource = (typeof GEOCODE_SOURCES)[number]
export const GEOCODE_SOURCE_LABELS: Record<GeocodeSource, string> = {
  manual: 'von Hand gesetzt',
  app: 'aus der Bestellung',
  geocoder: 'Adresssuche',
  approx: 'ungefähr (Postleitzahl)',
}

export const DEFAULT_DWELL_MINUTES = 4
export const DEFAULT_TOUR_START = '08:00'
/** Kapazität eines Fahrzeugs in Kasten, wenn nichts hinterlegt ist (T6) */
export const DEFAULT_VEHICLE_CAPACITY = 60
/** Kasten je Palette, wenn nur die Kapazität in Paletten hinterlegt ist (T6) */
export const CRATES_PER_PALLET = 40
